import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from '@/components/ui';
import {
  Mail,
  Lock,
  AlertCircle,
  ShieldCheck,
  Eye,
  EyeOff,
  InboxIcon,
  RefreshCw,
} from 'lucide-react';
import { INVITE_TOKEN_KEY } from '@/pages/InviteLandingPage';

/* ─────────────────────────────────────────────────────────────────────────────
   SignUpPage
   Route: /signup  (public)

   After a successful signUp() call Supabase sends a confirmation email before
   the session is active.  The old code auto-redirected after 2 s which was
   wrong — the user has no valid session yet.

   New flow:
   1. Form submitted → signUp() called.
   2. On success → hide the form entirely and show the full-screen
      "Check Your Email" state (Hebrew, Precision Architect style).
   3. The token remains in sessionStorage.  When the user clicks the
      confirmation link in their email, Supabase redirects them back with a
      valid session.  AuthContext / OnboardingCheck then detects the pending
      invite token and navigates to /join/<token> automatically.
   4. A "Resend Email" button re-calls signUp() (Supabase de-dupes it) and
      shows brief feedback without wiping the success screen.
────────────────────────────────────────────────────────────────────────────── */

export function SignUpPage() {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp }    = useAuth();

  /* Invite token: URL query param takes priority, sessionStorage as fallback */
  const inviteToken =
    searchParams.get('invite') ||
    sessionStorage.getItem(INVITE_TOKEN_KEY) ||
    '';

  /* ── Form state ── */
  const [formData, setFormData] = useState({
    email:           '',
    password:        '',
    confirmPassword: '',
  });

  /* ── UI state ── */
  const [error,               setError]               = useState<string | null>(null);
  const [success,             setSuccess]             = useState(false);
  const [loading,             setLoading]             = useState(false);
  const [resending,           setResending]           = useState(false);
  const [resendConfirm,       setResendConfirm]       = useState(false);
  const [showPassword,        setShowPassword]        = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /* ── Password strength ── */
  function getPasswordStrength(password: string): {
    strength: 'weak' | 'medium' | 'strong';
    color: string;
    width: string;
    text: string;
  } {
    if (!password) return { strength: 'weak', color: 'bg-gray-300', width: '0%',   text: '' };
    if (password.length < 6)  return { strength: 'weak',   color: 'bg-red-500',    width: '33%',  text: 'חלשה' };
    if (password.length < 10) return { strength: 'medium', color: 'bg-yellow-500', width: '66%',  text: 'בינונית' };
    const strong = /[A-Z]/.test(password) && /[a-z]/.test(password) &&
                   /\d/.test(password)    && /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return strong
      ? { strength: 'strong', color: 'bg-green-500', width: '100%', text: 'חזקה' }
      : { strength: 'medium', color: 'bg-yellow-500', width: '66%', text: 'בינונית' };
  }

  const passwordStrength = getPasswordStrength(formData.password);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* ── Submit ── */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('אנא מלא את כל השדות');
      setLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('כתובת האימייל אינה תקינה');
      setLoading(false);
      return;
    }
    if (formData.password.length < 8) {
      setError('הסיסמה חייבת להכיל לפחות 8 תווים');
      setLoading(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('הסיסמאות אינן תואמות');
      setLoading(false);
      return;
    }

    try {
      const { error: signUpError } = await signUp(formData.email, formData.password);
      if (signUpError) {
        setError(signUpError.message);
      } else {
        // ✅ Success — show "Check Your Email" screen.
        // Do NOT redirect yet: the user has no session until they click the
        // confirmation link.  The invite token stays in sessionStorage so
        // the post-confirmation redirect can pick it up.
        setSuccess(true);
      }
    } catch {
      setError('אירעה שגיאה בלתי צפויה. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Resend confirmation email ── */
  const handleResend = async () => {
    setResending(true);
    setResendConfirm(false);
    try {
      await signUp(formData.email, formData.password); // Supabase de-dupes silently
      setResendConfirm(true);
      setTimeout(() => setResendConfirm(false), 4000);
    } catch {
      // Ignore — the email was almost certainly already sent
    } finally {
      setResending(false);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     SUCCESS STATE — "Check Your Email"
     Replaces the entire page content; no form visible.
  ═══════════════════════════════════════════════════════════════════════════ */
  if (success) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50
                   flex items-center justify-center p-4"
        dir="rtl"
      >
        <div className="w-full max-w-md">

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">

            {/* Top accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-primary-400 via-primary-500 to-accent-500" />

            <div className="px-8 py-10 text-center">

              {/* Icon cluster */}
              <div className="relative inline-flex items-center justify-center mb-6">
                {/* Outer soft ring */}
                <div className="absolute inset-0 rounded-full bg-primary-100 opacity-40 scale-125" />
                {/* Inner ring */}
                <div className="absolute inset-0 rounded-full bg-primary-50" />
                {/* Icon circle */}
                <div className="relative w-20 h-20 rounded-full bg-primary-600 flex items-center justify-center
                                shadow-lg shadow-primary-200">
                  <InboxIcon className="w-9 h-9 text-white" strokeWidth={1.5} />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                מייל אימות נשלח אליך!
              </h1>

              {/* Sub-text */}
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                אנא בדוק את תיבת הדואר שלך
                {formData.email && (
                  <>
                    {' לכתובת '}
                    <span className="font-semibold text-gray-700 break-all">
                      {formData.email}
                    </span>
                  </>
                )}
                {' '}(כולל תיקיית ספאם) ולחץ על הקישור כדי להפעיל את החשבון
                ולהצטרף לצוות.
              </p>

              {/* Divider */}
              <div className="my-7 border-t border-gray-100" />

              {/* Steps */}
              <ol className="text-right space-y-3 mb-8">
                {[
                  { n: '1', text: 'פתח את תיבת הדואר שלך' },
                  { n: '2', text: 'מצא מייל מ-Axelio ולחץ על "אימות חשבון"' },
                  { n: '3', text: 'תועבר אוטומטית להמשך תהליך הצטרפות לצוות' },
                ].map(({ n, text }) => (
                  <li key={n} className="flex items-start gap-3">
                    <span
                      className="shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700
                                 text-xs font-bold flex items-center justify-center mt-0.5"
                    >
                      {n}
                    </span>
                    <span className="text-sm text-gray-600">{text}</span>
                  </li>
                ))}
              </ol>

              {/* Resend button */}
              <button
                onClick={handleResend}
                disabled={resending}
                className="inline-flex items-center gap-2 text-sm font-medium
                           text-primary-600 hover:text-primary-700
                           disabled:text-primary-300 transition-colors"
              >
                {resending
                  ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />שולח שוב…</>
                  : <><RefreshCw className="w-3.5 h-3.5" />לא קיבלת? שלח שוב</>
                }
              </button>

              {/* Resend confirmation flash */}
              {resendConfirm && (
                <p className="mt-2 text-xs text-green-600 font-medium animate-pulse">
                  ✓ המייל נשלח שוב
                </p>
              )}

              {/* Back to login */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <button
                  onClick={() => navigate('/login')}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors underline
                             underline-offset-2"
                >
                  חזרה לדף הכניסה
                </button>
              </div>
            </div>
          </div>

          {/* Security notice */}
          <p className="mt-6 text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            הנתונים שלך מוגנים בהצפנת SSL 256-bit
          </p>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     FORM STATE
  ═══════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50
                    flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo + header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
            <img src="/logo.svg" alt="Axelio Logo" className="w-full h-full drop-shadow-lg" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Join Axelio</h1>
          <p className="text-gray-600">Create your account to get started</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Create Account</h2>
            <p className="text-gray-600 text-sm">Fill in your details to register</p>
          </div>

          {/* Error alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Registration Failed</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email" name="email" type="email"
                  value={formData.email} onChange={handleChange}
                  placeholder="you@example.com"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg
                             focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  disabled={loading} autoComplete="email" required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password" name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password} onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg
                             focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  disabled={loading} autoComplete="new-password" required
                />
                <button
                  type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400
                             hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${passwordStrength.color} transition-all duration-300`}
                        style={{ width: passwordStrength.width }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600">
                      {passwordStrength.text}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Use 8+ characters with a mix of letters, numbers & symbols
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="confirmPassword" name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword} onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg
                             focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  disabled={loading} autoComplete="new-password" required
                />
                <button
                  type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400
                             hover:text-gray-600 transition-colors"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start">
              <input
                id="terms" type="checkbox"
                className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded
                           focus:ring-2 focus:ring-primary-500"
                required
              />
              <label htmlFor="terms" className="ml-3 text-sm text-gray-700">
                I agree to the{' '}
                <a href="#" className="text-primary-600 hover:text-primary-700 font-medium">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-primary-600 hover:text-primary-700 font-medium">
                  Privacy Policy
                </a>
              </label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full py-3 text-base font-semibold"
              isLoading={loading}
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Already have an account?</span>
            </div>
          </div>

          {/* Sign-in link — preserve invite token */}
          <Link to={inviteToken ? `/login?invite=${inviteToken}` : '/login'}>
            <Button type="button" variant="outline" className="w-full py-3 text-base font-semibold">
              Sign In
            </Button>
          </Link>
        </div>

        {/* Security notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Your data is protected with enterprise-grade security
          </p>
        </div>
      </div>
    </div>
  );
}
